import { toFullName } from '@repo/toolkit/to-full-name'
import {
  BellIcon,
  EmailIcon,
  PlusIcon,
  SendIcon,
  UploadIcon,
  UserRemoveIcon,
  UserThreeIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import isEqual from 'lodash.isequal'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'

import { affectedStaffUploadDpaeAcknowledgmentReceipt } from '../../../api/affected-staffs/affected-staff-uplaod-dpae-acknowledgment-receipt'
import { sendContracts } from '../../../api/affected-staffs/send-contract'
import { sendTravelChoiceReminderToStaffs } from '../../../api/affected-staffs/send-travel-choice-reminder-to-staffs'
import { API } from '../../../api/apis.config'
import { sendEmailToStaffs } from '../../../api/staff-application/send-email-to-staffs'
import {
  sendFinalContractReminderToStaffs,
  sendFinalMissingDocumentsReminderToStaffs
} from '../../../api/staff-application/send-final-reminder-to-staffs'
import {
  sendFriendlyContractReminderToStaffs,
  sendFriendlyMissingDocumentsReminderToStaffs
} from '../../../api/staff-application/send-friendly-reminder-to-staffs'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import UserNavBar from '../../../components/elements/nav-bar'
import { AffectedStaffsFilters } from '../../../components/pages/affected-staffs/affected-staffs-filters'
import { AffectedStaffsPdfChoicesForm } from '../../../components/pages/affected-staffs/affected-staffs-pdf-choices-form'
import CancelAffectedStaffModal from '../../../components/pages/affected-staffs/cancel-affected-staff/cancel-affected-staff-modal'
import { PdfFormat } from '../../../components/pages/affected-staffs/utils'
import SendEmailForm from '../../../components/pages/candidates/send-email-form'
import { EmailModal } from '../../../components/pages/common/email-modal'
import DpaeFormModal from '../../../components/pages/dpae-modal/dpae-form-modal'
import AffectedStaffTable from '../../../components/pages/staff/affected-staffs-table'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { AffectedStaffStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { AffectedStaff } from '../../../lib/types/models/affectedStaff/affected-staff'
import { AffectedStaffFilters } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformAffectedStaffFiltersToUrl,
  transformUrlToAffectedStaffFilters
} from '../../../lib/utils/filters-mapping/affected-staff'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

export const staffMonitoringInitialFilter: AffectedStaffFilters = {
  statuses: [AffectedStaffStatusEnum.AFFECTED, AffectedStaffStatusEnum.PENDING]
}

export const AffectedStaffsListPage = () => {
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    | 'send-email'
    | 'print-export'
    | 'filters'
    | 'upload-dpae-acknowledgment-receipt'
    | 'deactivate'
    | 'send-deaffection-email',
    AffectedStaff[]
  >([
    'send-email',
    'print-export',
    'filters',
    'upload-dpae-acknowledgment-receipt',
    'deactivate',
    'send-deaffection-email'
  ])

  const { downloadDocuments } = useDownloadDocuments()

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<AffectedStaffFilters>({
      initialFilters: staffMonitoringInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformAffectedStaffFiltersToUrl,
      transformUrlToFilter: transformUrlToAffectedStaffFilters
    })

  const { mutateAsync: sendEmailToStaffsMutation } = useMutation({
    mutationFn: sendEmailToStaffs,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  const { mutateAsync: sendFriendlyContractReminderToStaffsMutation } = useMutation({
    mutationFn: sendFriendlyContractReminderToStaffs,
    onError: showAxiosError
  })

  const { mutateAsync: sendFriendlyMissingDocumentsReminderToStaffsMutation } = useMutation({
    mutationFn: sendFriendlyMissingDocumentsReminderToStaffs,
    onError: showAxiosError
  })

  const { mutateAsync: sendFinalContractReminderToStaffsMutation } = useMutation({
    mutationFn: sendFinalContractReminderToStaffs,
    onError: showAxiosError
  })

  const { mutateAsync: sendFinalMissingDocumentsReminderToStaffsMutation } = useMutation({
    mutationFn: sendFinalMissingDocumentsReminderToStaffs,
    onError: showAxiosError
  })

  const { mutateAsync: sendTravelChoiceReminderToStaffsMutation } = useMutation({
    mutationFn: sendTravelChoiceReminderToStaffs,
    onError: showAxiosError
  })

  const uploadDpae = useMutation({
    mutationFn: affectedStaffUploadDpaeAcknowledgmentReceipt,
    onSuccess: () => {
      toast.success('Le document a été mis à jour avec succès')
      closeModel('upload-dpae-acknowledgment-receipt')
    },
    onError: showAxiosError
  })

  // the staffs that did not get the contract are named by the backend with the reason
  const sendContractsMutation = useMutation({
    mutationFn: sendContracts,
    onSuccess: ({ message, partial }) => {
      if (partial) toast.warning(message, { duration: 10000 })
      else toast.success(message)
    },
    onError: showAxiosError
  })

  // Shared helpers to DRY validation + mutation flows
  const remindContracts = async (affectedStaffs: AffectedStaff[], isFinal = false) => {
    const ids = affectedStaffs.map((s) => s.id)
    const { message } = isFinal
      ? await sendFinalContractReminderToStaffsMutation({ ids })
      : await sendFriendlyContractReminderToStaffsMutation({ ids })
    toast.success(message)
  }

  // the backend skips the staffs with nothing missing, the message stays the same for everyone
  const remindMissingDocuments = async (affectedStaffs: AffectedStaff[], isFinal = false) => {
    const ids = affectedStaffs.map((s) => s.id)
    const { message } = isFinal
      ? await sendFinalMissingDocumentsReminderToStaffsMutation({ ids })
      : await sendFriendlyMissingDocumentsReminderToStaffsMutation({ ids })
    toast.success(message)
  }

  const emails = [...new Set(selectedData?.map((row) => row.staff.user.email))]

  return (
    <div>
      <UserNavBar title={'Suivi des encadrants'} icon={UserThreeIcon} />
      <div className={'h-4'} />
      <AffectedStaffTable
        openInNewTab
        filterOptions={{
          isTableLoading: isTableLoading,
          setIsTableLoading: setIsTableLoading,
          selectedFilter: selectedFilter,
          onFiltersClick: () => toggleModal('filters'),
          discardedFilters: isEqual(
            selectedFilter?.statuses,
            staffMonitoringInitialFilter.statuses
          )
            ? ['statuses']
            : []
        }}
        actions={(tableRef) => [
          {
            type: 'multiple',
            icon: SendIcon,
            label: 'Envoyer le contrat',
            variant: 'ghost',
            refetch: true,
            fn: async (affectedStaffs) => {
              await sendContractsMutation.mutateAsync({ ids: affectedStaffs.map((s) => s.id) })
            }
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Relance contrat',
            variant: 'ghost',
            refetch: true,
            fn: async (affectedStaffs) => remindContracts(affectedStaffs)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Derniere relance contrat',
            variant: 'ghost',
            refetch: true,
            fn: async (affectedStaffs) => remindContracts(affectedStaffs, true)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Relance document(s) manquant(s)',
            variant: 'ghost',
            refetch: true,
            fn: async (affectedStaffs) => remindMissingDocuments(affectedStaffs)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Derniere relance document(s) manquant(s)',
            variant: 'ghost',
            refetch: true,
            fn: async (affectedStaffs) => remindMissingDocuments(affectedStaffs, true)
          },
          {
            type: 'multiple',
            icon: BellIcon,
            label: 'Relance travel choice',
            variant: 'ghost',
            refetch: true,
            fn: async (affectedStaffs) =>
              sendTravelChoiceReminderToStaffsMutation({ ids: affectedStaffs.map((s) => s.id) })
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            label: 'Envoyer email groupé',
            variant: 'primary',
            fn: (staffs) => toggleModal('send-email', staffs)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            label: 'Exporter les diplomes',
            variant: 'ghost',
            fn: (staffs) => {
              const ids = staffs.map((staff) => staff.id)
              downloadDocuments({
                endpoint: API.affectedStaffs.exportStaffsDiplomasToExcel.url,
                ids
              })
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            label: 'Exporter les fiches A4',
            variant: 'ghost',
            fn: (staffs) => toggleModal('print-export', staffs)
          },
          {
            type: 'multiple',
            icon: PlusIcon,
            label: 'Générer les DPAE',
            variant: 'ghost',
            fn: async (staffs) => {
              const ids = staffs.map((staff) => staff.id)
              await downloadDocuments({ ids, endpoint: API.affectedStaffs.exportDpaeXml.url })
              tableRef.current?.refetch()
            }
          },
          {
            type: 'global',
            icon: UploadIcon,
            label: 'Importer AR DPAE',
            variant: 'ghost',
            fn: () => toggleModal('upload-dpae-acknowledgment-receipt')
          },
          {
            type: 'multiple',
            icon: UserRemoveIcon,
            label: 'Désaffecter',
            variant: 'destructive',
            fn: (affectedStaff) => toggleModal('deactivate', affectedStaff)
          }
        ]}
      />

      {selectedData && (
        <Fragment>
          {/* export pdf */}
          <Modal
            isOpen={modalState['print-export']}
            title={'Exportation des Fiches en PDF (A4)'}
            icon={UploadIcon}
            hideActions
            onCancel={() => closeModel('print-export')}
          >
            <AffectedStaffsPdfChoicesForm
              className={MODAL_BODY_CLASSNAME}
              handleSubmit={async ({ format }) => {
                await downloadDocuments({
                  endpoint:
                    format === PdfFormat.complete
                      ? API.staffs.printCompletePdf.url
                      : API.staffs.printPartialPdf.url,
                  ids: selectedData.map((affecteStaff) => affecteStaff.staff.id)
                })

                closeModel('print-export')
              }}
              submitText={'Exporter'}
              onCancel={() => closeModel('print-export')}
            />
          </Modal>

          {/* Send email */}
          <Modal
            isOpen={modalState['send-email']}
            title={'Envoyer un email'}
            icon={EmailIcon}
            size="medium"
            hideActions
            onCancel={() => toggleModal('send-email')}
          >
            <SendEmailForm
              className={MODAL_BODY_CLASSNAME}
              defaultValues={{
                emails,
                subject: '',
                body: ''
              }}
              labels={[
                ...new Map(
                  selectedData.map((affectedStaff) => [
                    affectedStaff.staff.user.email,
                    `${toFullName(affectedStaff.staff.firstName, affectedStaff.staff.lastName)} - ${affectedStaff.staff.user.email}`
                  ])
                ).values()
              ]}
              buttonsConfig={modalButtonConfig({
                onCancel: () => toggleModal('send-email'),
                confirmText: 'Envoyer',
                onConfirm: (values) => sendEmailToStaffsMutation({ ...values, emails })
              })}
            />
          </Modal>
        </Fragment>
      )}

      <DpaeFormModal
        isOpen={modalState['upload-dpae-acknowledgment-receipt']}
        buttonsConfig={modalButtonConfig({
          isLoading: uploadDpae.isPending,
          onConfirm: uploadDpae.mutate,
          onCancel: () => closeModel('upload-dpae-acknowledgment-receipt')
        })}
        onCancel={() => closeModel('upload-dpae-acknowledgment-receipt')}
      />

      {/* Filters Modal */}

      {selectedData && (
        <CancelAffectedStaffModal
          title={`Êtes-vous sûr(e) de vouloir désaffecter "${selectedData.map((af) => `${toFullName(af.staff.firstName, af.staff.lastName)}`).join(', ')}" ?`}
          affectedStaffIds={selectedData.map((af) => af.id)}
          isOpen={modalState.deactivate}
          onCancel={() => toggleModal('deactivate')}
          onSuccess={({ centerLocked }) => {
            // a staff left a locked center: offer to notify the LEC users
            if (centerLocked) toggleModal('send-deaffection-email')
          }}
        />
      )}

      <EmailModal
        emailApiEndpoint={API.affectedStaffs.sendAffectedStaffDeaffectionNotification.url}
        label="Vous vous apprêtez à désaffecter un encadrant sur un centre verrouillé. Souhaitez-vous envoyer une notification ?"
        isOpen={modalState['send-deaffection-email']}
        closeModal={() => closeModel('send-deaffection-email')}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <AffectedStaffsFilters filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
